import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { keys, useMe } from '../lib/queries'
import { AgentSection } from './AgentSection'
import { handleFor } from '../lib/format'
import type { NotificationPref, User } from '../lib/types'
import { Button, Field, Input, Select, Spinner, cx, useTheme, useToast, type ThemeMode } from '../components/ui'

export function SettingsPage({ user }: { user: User }) {
  const [params, setParams] = useSearchParams()
  const toast = useToast()

  // The Slack OAuth round trip comes back with a marker in the URL; report the
  // outcome once, then clean the address bar.
  useEffect(() => {
    const slack = params.get('slack')
    if (!slack) return
    if (slack === 'linked') toast.show('Slack account linked')
    if (slack === 'cancelled') toast.show('Slack linking was cancelled')
    const next = new URLSearchParams(params)
    next.delete('slack')
    setParams(next, { replace: true })
  }, [params, setParams, toast])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[640px] px-5 py-6">
        <h1 className="text-[17px] font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-[13px] text-[var(--ink-2)]">
          Your profile, what you get told about, and the keys other tools use.
        </p>

        <AppearanceSection />
        <ProfileSection user={user} />
        <PasswordSection />
        <NotificationSection />
        <SlackSection />
        <AgentSection />
      </div>
    </div>
  )
}

function Section({
  title, hint, children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="mt-7 border-t border-[var(--line)] pt-6">
      <h2 className="text-[14px] font-semibold">{title}</h2>
      {hint && <p className="mt-0.5 text-[12.5px] text-[var(--ink-2)]">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

// ------------------------------------------------------------- appearance

const THEMES: Array<{ id: ThemeMode; label: string; hint: string }> = [
  { id: 'system', label: 'System', hint: 'Follow the operating system' },
  { id: 'light', label: 'Light', hint: 'Always light' },
  { id: 'dark', label: 'Dark', hint: 'Always dark' },
]

function AppearanceSection() {
  const { mode, setMode, dark } = useTheme()

  return (
    <Section
      title="Appearance"
      hint="There is also a one-click toggle at the bottom of the sidebar."
    >
      <div className="flex flex-wrap gap-2">
        {THEMES.map((t) => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            aria-pressed={mode === t.id}
            className={cx(
              'flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors duration-[140ms]',
              mode === t.id
                ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
                : 'border-[var(--line)] hover:border-[var(--line-strong)]',
            )}
          >
            <span className="flex items-center gap-2">
              {/* A miniature of the theme itself, so the choice is visible
                  rather than a word you have to try. */}
              <span
                aria-hidden
                className="flex h-5 w-7 shrink-0 overflow-hidden rounded border border-[var(--line-strong)]"
              >
                <span className="w-1/2" style={{ background: t.id === 'dark' ? '#0c0d11' : '#fbfbfd' }} />
                <span className="w-1/2" style={{ background: t.id === 'light' ? '#fbfbfd' : '#0c0d11' }} />
              </span>
              <span className="text-[13px] font-medium">{t.label}</span>
            </span>
            <span className="mt-1 block text-[11.5px] text-[var(--ink-3)]">{t.hint}</span>
          </button>
        ))}
      </div>

      <p className="mt-2 text-[12px] text-[var(--ink-3)]">
        Currently showing the {dark ? 'dark' : 'light'} theme.
      </p>
    </Section>
  )
}

// ---------------------------------------------------------------- profile

function ProfileSection({ user }: { user: User }) {
  const [name, setName] = useState(user.name)
  const [timezone, setTimezone] = useState(user.timezone)
  const [digestEmail, setDigestEmail] = useState(user.digestEmail)
  const queryClient = useQueryClient()
  const toast = useToast()

  const zones =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [user.timezone, 'UTC']

  const save = useMutation({
    mutationFn: () => api.updateProfile({ name, timezone, avatarUrl: user.avatarUrl, digestEmail }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.me() })
      toast.show('Profile saved')
    },
  })
  const error = save.error instanceof ApiError ? save.error : null

  return (
    <Section title="Profile">
      <div className="space-y-4">
        <Field label="Name" htmlFor="name" error={error?.fields?.name}>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field
          label="Email" htmlFor="email"
          hint={`Others mention you as @${handleFor(user.email)}. Ask an administrator to change your address.`}
        >
          <Input id="email" value={user.email} disabled />
        </Field>

        <Field label="Timezone" htmlFor="tz" error={error?.fields?.timezone}
          hint="Used to decide when your daily summary goes out.">
          <Select id="tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {[...new Set([timezone, ...zones])].map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </Select>
        </Field>

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={digestEmail}
            onChange={(e) => setDigestEmail(e.target.checked)}
            className="mt-0.5 accent-[var(--accent)]"
          />
          <span>
            <span className="block text-[13px]">Send me a daily summary</span>
            <span className="block text-[12px] text-[var(--ink-3)]">
              One email each morning listing what is due today and what is late.
            </span>
          </span>
        </label>

        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
          Save profile
        </Button>
      </div>
    </Section>
  )
}

// --------------------------------------------------------------- password

function PasswordSection() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const toast = useToast()

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      toast.show('Password changed')
    },
  })
  const error = change.error instanceof ApiError ? change.error : null

  const signOutEverywhere = useMutation({
    mutationFn: api.logoutEverywhere,
    onSuccess: () => window.location.assign('/login'),
  })

  return (
    <Section title="Password">
      <div className="space-y-4">
        <Field label="Current password" htmlFor="cur" error={error?.fields?.currentPassword}>
          <Input
            id="cur" type="password" autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field
          label="New password" htmlFor="new"
          error={error?.fields?.newPassword} hint="At least 10 characters."
        >
          <Input
            id="new" type="password" autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)}
          />
        </Field>

        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => change.mutate()}
            loading={change.isPending}
            disabled={!current || !next}
          >
            Change password
          </Button>
          <Button
            variant="ghost"
            onClick={() => signOutEverywhere.mutate()}
            loading={signOutEverywhere.isPending}
          >
            Sign out everywhere
          </Button>
        </div>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------- notifications

function NotificationSection() {
  const prefs = useQuery({ queryKey: keys.prefs(), queryFn: api.prefs })
  const queryClient = useQueryClient()
  const toast = useToast()
  const me = useMe()

  const save = useMutation({
    mutationFn: (next: NotificationPref[]) => api.setPrefs(next),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.prefs(), data)
      toast.show('Notification settings saved')
    },
  })

  if (prefs.isLoading || !prefs.data) {
    return (
      <Section title="Notifications">
        <Spinner className="text-[var(--ink-3)]" />
      </Section>
    )
  }

  const emailOff = me.data && !me.data.emailConfigured

  function toggle(event: string, channel: 'email' | 'slack', value: boolean) {
    const next = prefs.data!.prefs.map((p) =>
      p.event === event ? { ...p, [channel]: value } : p,
    )
    save.mutate(next)
  }

  return (
    <Section
      title="Notifications"
      hint="You are never told about your own actions, and a burst of comments arrives as one message."
    >
      {emailOff && (
        <p className="mb-3 rounded-md border border-[var(--line)] bg-[var(--sunken)] px-3 py-2 text-[12.5px] text-[var(--ink-2)]">
          Email delivery is not configured on this installation, so email
          notifications will not arrive until an administrator sets it up.
        </p>
      )}

      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-[var(--line)] text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
            <th className="py-2 text-left font-medium">Tell me when</th>
            <th className="w-16 py-2 text-center font-medium">Email</th>
            <th className="w-16 py-2 text-center font-medium">Slack</th>
          </tr>
        </thead>
        <tbody>
          {prefs.data.prefs.map((p) => (
            <tr key={p.event} className="border-b border-[var(--line)] last:border-0">
              <td className="py-2 pr-3 text-[var(--ink-2)]">
                {prefs.data!.labels[p.event] ?? p.event}
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={p.email}
                  onChange={(e) => toggle(p.event, 'email', e.target.checked)}
                  aria-label={`Email me when ${prefs.data!.labels[p.event]}`}
                  className="accent-[var(--accent)]"
                />
              </td>
              <td className="py-2 text-center">
                <input
                  type="checkbox"
                  checked={p.slack}
                  onChange={(e) => toggle(p.event, 'slack', e.target.checked)}
                  aria-label={`Send me a Slack message when ${prefs.data!.labels[p.event]}`}
                  className="accent-[var(--accent)]"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

// ------------------------------------------------------------------ slack

function SlackSection() {
  const slack = useQuery({ queryKey: keys.slack(), queryFn: api.slackIdentity })
  const queryClient = useQueryClient()

  const unlink = useMutation({
    mutationFn: api.unlinkSlack,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.slack() }),
  })

  if (!slack.data) return null

  if (!slack.data.canConnect) {
    return (
      <Section title="Slack">
        <p className="text-[13px] text-[var(--ink-2)]">
          Slack is not set up on this installation. An administrator needs to add the
          app credentials before accounts can be linked.
        </p>
      </Section>
    )
  }

  return (
    <Section
      title="Slack"
      hint="Link your account once and notifications arrive as direct messages from the bot."
    >
      {slack.data.linked ? (
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 text-[13px]">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--good)]" />
            Linked as
            <span className="task-key text-[12px] text-[var(--ink-2)]">
              {slack.data.identity.slackUserId}
            </span>
          </span>
          <Button size="sm" onClick={() => unlink.mutate()} loading={unlink.isPending}>
            Unlink
          </Button>
        </div>
      ) : (
        <a href="/api/me/slack/connect">
          <Button variant="primary">Link my Slack account</Button>
        </a>
      )}
    </Section>
  )
}

